import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/core/AuthContext';
import { useLanguage } from '@/core/LanguageContext';
import { useToast } from '@/shared/ui/use-toast';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Progress } from '@/shared/ui/progress';
import { Textarea } from '@/shared/ui/textarea';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Loader2,
  MapPin,
  MoreHorizontal,
  Paperclip,
  ShieldAlert,
  ShieldCheck,
  SkipForward,
  XCircle,
} from 'lucide-react';
import { useInstancesByAuditorId } from '../hooks/useScheduleInstances';
import {
  useCancelCompletion,
  useCompleteCompletion,
  useCompletionById,
  useCompletionSummary,
  useSaveCompletionResponse,
  useStartCompletion,
} from '../hooks/useCompletions';
import { useDeleteResponseAttachment, useUploadResponseAttachment } from '../hooks/useResponseAttachments';
import { createAttachmentSignedUrl } from '../api/responseAttachments';

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0m';
  if (seconds < 60) return '<1m';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}

function formatRelativeDue(dueDate: string): string {
  const now = Date.now();
  const dueMs = new Date(dueDate).getTime();
  const diff = dueMs - now;
  const absSeconds = Math.round(Math.abs(diff) / 1000);

  if (absSeconds < 60) {
    return diff >= 0 ? 'Due in under 1m' : 'Overdue by under 1m';
  }

  const absMinutes = Math.round(absSeconds / 60);
  if (absMinutes < 60) {
    return diff >= 0 ? `Due in ${absMinutes}m` : `Overdue by ${absMinutes}m`;
  }

  const absHours = Math.round(absMinutes / 60);
  if (absHours < 48) {
    return diff >= 0 ? `Due in ${absHours}h` : `Overdue by ${absHours}h`;
  }

  const absDays = Math.round(absHours / 24);
  return diff >= 0 ? `Due in ${absDays}d` : `Overdue by ${absDays}d`;
}

function ConformanceButtons({
  value,
  onSelect,
  t,
}: {
  value: 'conforming' | 'non_conforming' | 'not_applicable' | 'skipped' | 'question_disabled' | null;
  onSelect: (status: 'conforming' | 'non_conforming' | 'not_applicable' | 'skipped') => void;
  t: (key: string, fallback: string) => string;
}) {
  const hasSecondaryChoice = value === 'not_applicable' || value === 'skipped';

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          variant={value === 'conforming' ? 'default' : 'outline'}
          className={value === 'conforming' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          onClick={() => onSelect('conforming')}
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          {t('mod_lpa.completions.conforming', 'Conforming')}
        </Button>
        <Button
          variant={value === 'non_conforming' ? 'default' : 'outline'}
          className={value === 'non_conforming' ? 'bg-red-600 hover:bg-red-700' : ''}
          onClick={() => onSelect('non_conforming')}
        >
          <ShieldAlert className="h-4 w-4 mr-2" />
          {t('mod_lpa.completions.nonConforming', 'Non-conforming')}
        </Button>
      </div>

      <div className="flex items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={hasSecondaryChoice ? 'secondary' : 'outline'}
              size="sm"
            >
              <MoreHorizontal className="h-4 w-4 mr-2" />
              {t('mod_lpa.completions.moreOptions', 'More options')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onSelect('not_applicable')}>
              {t('mod_lpa.completions.notApplicable', 'N/A')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onSelect('skipped')}>
              <SkipForward className="h-4 w-4 mr-2" />
              {t('mod_lpa.completions.skip', 'Skip')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function MyAuditsPage() {
  const { profile } = useAuth();
  const { t, getTranslation } = useLanguage();
  const { toast } = useToast();

  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [activeCompletionId, setActiveCompletionId] = useState<string | null>(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionStartedAt, setQuestionStartedAt] = useState<number>(Date.now());
  const [questionCommentDraft, setQuestionCommentDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const startCompletion = useStartCompletion();
  const saveResponse = useSaveCompletionResponse();
  const completeCompletion = useCompleteCompletion();
  const cancelCompletion = useCancelCompletion();
  const uploadAttachment = useUploadResponseAttachment();
  const deleteAttachment = useDeleteResponseAttachment();

  const { data: instances, isLoading: isLoadingInstances } = useInstancesByAuditorId(profile?.id, {
    statuses: ['pending', 'in_progress', 'overdue', 'completed'],
  });

  const {
    data: completion,
    isLoading: isLoadingCompletion,
    refetch: refetchCompletion,
  } = useCompletionById(activeCompletionId);

  const { data: completionSummary } = useCompletionSummary(activeCompletionId);

  const openInstances = useMemo(
    () =>
      (instances ?? []).filter((i) => {
        if (!['pending', 'in_progress', 'overdue'].includes(i.status)) return false;
        return true;
      }),
    [instances],
  );

  const sortedOpenInstances = useMemo(() => {
    return [...openInstances].sort((a, b) => {
      const dueDiff = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      if (dueDiff !== 0) return dueDiff;

      const statusPriority = (status: string) => (status === 'in_progress' ? 0 : 1);
      return statusPriority(a.status) - statusPriority(b.status);
    });
  }, [openInstances]);

  const overdueCount = useMemo(
    () =>
      openInstances.filter(
        (instance) => new Date(instance.due_date).getTime() < Date.now(),
      ).length,
    [openInstances],
  );

  const completed = useMemo(
    () =>
      (instances ?? []).filter((i) => {
        if (i.status !== 'completed' || !i.completed_at) return false;
        const completedMs = new Date(i.completed_at).getTime();
        return completedMs >= Date.now() - 7 * 24 * 60 * 60 * 1000;
      }),
    [instances],
  );

  const visibleResponses = useMemo(
    () => (completion?.responses ?? []).filter((r) => r.is_question_active),
    [completion?.responses],
  );

  const currentResponse = visibleResponses[questionIndex] ?? null;
  const answeredCount = visibleResponses.filter(
    (r) => r.conformance_status && r.conformance_status !== 'question_disabled',
  ).length;
  const progressPct = visibleResponses.length > 0 ? (answeredCount / visibleResponses.length) * 100 : 0;

  useEffect(() => {
    if (!completionDialogOpen) return;
    setQuestionStartedAt(Date.now());
  }, [questionIndex, completionDialogOpen]);

  useEffect(() => {
    if (!currentResponse) return;
    setQuestionCommentDraft(currentResponse.comment ?? '');
  }, [currentResponse]);

  useEffect(() => {
    if (!completion) return;
    setNotesDraft(completion.notes ?? '');
  }, [completion]);

  useEffect(() => {
    let cancelled = false;

    const attachments = currentResponse?.attachments ?? [];
    if (attachments.length === 0) {
      setAttachmentUrls({});
      return () => {
        cancelled = true;
      };
    }

    void Promise.all(
      attachments.map(async (attachment) => {
        try {
          const url = await createAttachmentSignedUrl(attachment.file_path, 60 * 60);
          return [attachment.id, url] as const;
        } catch {
          return [attachment.id, ''] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const [id, url] of entries) {
        if (url) map[id] = url;
      }
      setAttachmentUrls(map);
    });

    return () => {
      cancelled = true;
    };
  }, [currentResponse]);

  const handleStart = async (instanceId: string) => {
    const completionRow = await startCompletion.mutateAsync(instanceId);
    setActiveCompletionId(completionRow.id);
    setQuestionIndex(0);
    setCompletionDialogOpen(true);
  };

  const handleSelectConformance = async (
    status: 'conforming' | 'non_conforming' | 'not_applicable' | 'skipped',
  ) => {
    if (!completion || !currentResponse) return;

    await saveResponse.mutateAsync({
      completionId: completion.id,
      input: {
        schedule_question_id: currentResponse.schedule_question_id,
        conformance_status: status,
        comment: questionCommentDraft,
        duration_seconds: Math.max(0, Math.round((Date.now() - questionStartedAt) / 1000)),
      },
    });

    await refetchCompletion();
  };

  const handleSaveComment = async () => {
    if (
      !completion ||
      !currentResponse ||
      !currentResponse.conformance_status ||
      currentResponse.conformance_status === 'question_disabled'
    ) {
      return;
    }

    await saveResponse.mutateAsync({
      completionId: completion.id,
      input: {
        schedule_question_id: currentResponse.schedule_question_id,
        conformance_status: currentResponse.conformance_status,
        comment: questionCommentDraft,
        duration_seconds: currentResponse.duration_seconds,
      },
    });
  };

  const handleUpload = async (file: File) => {
    if (!completion || !currentResponse?.id) {
      toast({
        title: t('mod_lpa.completions.answerBeforeAttachment', 'Answer the question first'),
        description: t(
          'mod_lpa.completions.answerBeforeAttachmentHint',
          'Add a conformance status before uploading an attachment.',
        ),
        variant: 'destructive',
      });
      return;
    }

    await uploadAttachment.mutateAsync({
      responseId: currentResponse.id,
      file,
      completionId: completion.id,
    });

    await refetchCompletion();
  };

  const handleDeleteAttachment = async (attachmentId: string, responseId: string) => {
    if (!completion) return;
    await deleteAttachment.mutateAsync({
      attachmentId,
      responseId,
      completionId: completion.id,
    });
    await refetchCompletion();
  };

  const handleComplete = async () => {
    if (!completion) return;
    await completeCompletion.mutateAsync({
      completionId: completion.id,
      input: { notes: notesDraft },
    });
    await refetchCompletion();
  };

  const handleCancel = async () => {
    if (!completion) return;
    await cancelCompletion.mutateAsync(completion.id);
    setCompletionDialogOpen(false);
    setActiveCompletionId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t('mod_lpa.myAudits.title', 'My Audits')}</h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'mod_lpa.myAudits.description',
              'Run your assigned audits, capture evidence, and close the loop in one flow.',
            )}
          </p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon">
              <CircleHelp className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-sm" align="end">
            {t(
              'mod_lpa.myAudits.help',
              'Audits are sorted by urgency: most overdue first, then nearest due dates. Start or continue an audit to answer questions one by one, add comments, and upload evidence files.',
            )}
          </PopoverContent>
        </Popover>
      </div>

      {isLoadingInstances ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {t('mod_lpa.myAudits.assigned', 'Assigned audits')}
                {overdueCount > 0 && <Badge variant="destructive">{overdueCount}</Badge>}
                {overdueCount === 0 && sortedOpenInstances.length > 0 && (
                  <Badge variant="secondary">
                    {t('mod_lpa.myAudits.onTrack', 'On track')}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  'mod_lpa.myAudits.urgencyHint',
                  'Sorted by urgency: most overdue first, then nearest due date.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {sortedOpenInstances.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('mod_lpa.myAudits.noneUpcoming', 'No upcoming audits assigned.')}</p>
              ) : (
                sortedOpenInstances.map((instance) => {
                  const isOverdue = new Date(instance.due_date).getTime() < Date.now();

                  return (
                  <Card key={instance.id} className={isOverdue ? 'border-red-200/60' : ''}>
                    <CardContent className="p-3 flex items-center justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="font-medium text-sm truncate">{instance.audit.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {instance.audit.location_label ?? t('mod_lpa.audits.entireFactory', 'Entire factory')}
                          </span>
                          <span>{instance.schedule.name}</span>
                          <span>{formatRelativeDue(instance.due_date)}</span>
                          <span>
                            {instance.schedule.question_count ?? 0} q •{' '}
                            {formatDuration(instance.schedule.estimated_duration_seconds ?? 0)}
                          </span>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleStart(instance.id)} disabled={startCompletion.isPending}>
                        {instance.status === 'in_progress' ? t('mod_lpa.myAudits.continue', 'Continue') : t('mod_lpa.myAudits.start', 'Start audit')}
                      </Button>
                    </CardContent>
                  </Card>
                );
                })
              )}
            </CardContent>
          </Card>

          <Collapsible open={completedOpen} onOpenChange={setCompletedOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <button type="button" className="w-full text-left">
                  <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        {t('mod_lpa.myAudits.completed', 'Completed (7 days)')}
                        <Badge variant="secondary">{completed.length}</Badge>
                      </CardTitle>
                      <ChevronDown className={`h-4 w-4 transition-transform ${completedOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-2 pt-0">
                  {completed.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('mod_lpa.myAudits.noneCompleted', 'No recently completed audits.')}</p>
                  ) : (
                    completed.map((instance) => (
                      <Card key={instance.id}>
                        <CardContent className="p-3">
                          <div className="font-medium text-sm">{instance.audit.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {instance.schedule.name} • {t('mod_lpa.myAudits.finishedAt', 'Finished at')} {new Date(instance.completed_at ?? '').toLocaleString()}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </>
      )}

      <Dialog
        open={completionDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setActiveCompletionId(null);
          }
          setCompletionDialogOpen(open);
        }}
      >
        <DialogContent className="w-[min(96vw,1200px)] max-w-5xl h-[92vh] max-h-[92vh] flex flex-col overflow-hidden p-0 gap-0">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b">
            <DialogTitle>
              {completion?.instance.audit.name ?? t('mod_lpa.completions.loadingTitle', 'Audit execution')}
            </DialogTitle>
            <DialogDescription>
              {completion?.instance.schedule.name ?? ''}
            </DialogDescription>
          </DialogHeader>

          {isLoadingCompletion || !completion ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm px-4 sm:px-6">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('mod_lpa.completions.loading', 'Loading completion...')}
            </div>
          ) : completion.status !== 'in_progress' ? (
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
              <div className="min-h-full flex flex-col items-center justify-center text-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mb-3" />
                <h3 className="text-lg font-semibold mb-1">
                  {t('mod_lpa.completions.doneTitle', 'Audit completed')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('mod_lpa.completions.doneHint', 'Great work. This audit is now closed and visible in completed history.')}
                </p>
                {completionSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full max-w-2xl text-sm">
                    <Card><CardContent className="p-3">Conforming: {completionSummary.counts.conforming}</CardContent></Card>
                    <Card><CardContent className="p-3">Non-conforming: {completionSummary.counts.non_conforming}</CardContent></Card>
                    <Card><CardContent className="p-3">Skipped: {completionSummary.counts.skipped}</CardContent></Card>
                    <Card><CardContent className="p-3">Duration: {formatDuration(completionSummary.duration_seconds)}</CardContent></Card>
                  </div>
                )}
                <Button className="mt-5" onClick={() => setCompletionDialogOpen(false)}>
                  {t('mod_lpa.completions.backToList', 'Back to My Audits')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="flex flex-col gap-3">
                <Card className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between text-sm gap-2">
                      <span>
                        {t('mod_lpa.completions.questionXofY', 'Question {current} of {total}')
                          .replace('{current}', String(Math.min(questionIndex + 1, Math.max(visibleResponses.length, 1))))
                          .replace('{total}', String(visibleResponses.length))}
                      </span>
                      <span className="text-muted-foreground inline-flex items-center gap-1 shrink-0">
                        <Clock3 className="h-3.5 w-3.5" />
                        {formatDuration(Math.round((Date.now() - new Date(completion.started_at).getTime()) / 1000))}
                      </span>
                    </div>
                    <Progress value={progressPct} />
                  </CardContent>
                </Card>

                {currentResponse ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <Card className="lg:col-span-2 min-h-0">
                    <CardHeader>
                      <CardTitle className="text-base leading-snug">
                        {getTranslation(currentResponse.question.question_translations, currentResponse.question.question)}
                      </CardTitle>
                      {currentResponse.question.explanation && (
                        <CardDescription>
                          {getTranslation(
                            currentResponse.question.explanation_translations,
                            currentResponse.question.explanation,
                          )}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <ConformanceButtons
                        value={currentResponse.conformance_status}
                        onSelect={handleSelectConformance}
                        t={t}
                      />

                      {currentResponse.conformance_status === 'non_conforming' &&
                        currentResponse.question.reaction_plan && (
                          <Card className="bg-amber-50 border-amber-300 dark:bg-amber-950/20 dark:border-amber-800">
                            <CardContent className="p-3 text-sm">
                              <strong>{t('mod_lpa.completions.reactionPlan', 'Reaction plan')}:</strong>{' '}
                              {getTranslation(
                                currentResponse.question.reaction_plan_translations,
                                currentResponse.question.reaction_plan,
                              )}
                            </CardContent>
                          </Card>
                        )}

                      <div className="space-y-2">
                        <Textarea
                          value={questionCommentDraft}
                          onChange={(e) => setQuestionCommentDraft(e.target.value)}
                          placeholder={t('mod_lpa.completions.commentPlaceholder', 'Add comment or findings...')}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="secondary"
                            className="w-full sm:w-auto"
                            onClick={handleSaveComment}
                            disabled={saveResponse.isPending || !currentResponse.conformance_status}
                          >
                            {t('mod_lpa.completions.saveComment', 'Save comment')}
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadAttachment.isPending}
                          >
                            <Paperclip className="h-4 w-4 mr-2" />
                            {t('mod_lpa.completions.addAttachment', 'Add attachment')}
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => cameraInputRef.current?.click()}
                            disabled={uploadAttachment.isPending}
                          >
                            <Camera className="h-4 w-4 mr-2" />
                            {t('mod_lpa.completions.takePhoto', 'Take photo')}
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                void handleUpload(file);
                              }
                              e.currentTarget.value = '';
                            }}
                          />
                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                void handleUpload(file);
                              }
                              e.currentTarget.value = '';
                            }}
                          />
                        </div>
                      </div>

                      {currentResponse.attachments.length > 0 && (
                        <div className="space-y-2">
                          <Separator />
                          {currentResponse.attachments.map((attachment) => (
                            <div
                              key={attachment.id}
                              className="flex items-center justify-between gap-2 text-sm rounded border px-2 py-1.5"
                            >
                              {attachmentUrls[attachment.id] ? (
                                <a
                                  href={attachmentUrls[attachment.id]}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="truncate underline"
                                >
                                  {attachment.file_name}
                                </a>
                              ) : (
                                <span className="truncate text-muted-foreground">
                                  {attachment.file_name}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() =>
                                  handleDeleteAttachment(
                                    attachment.id,
                                    currentResponse.id ?? '',
                                  )
                                }
                              >
                                {t('common.delete', 'Delete')}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{t('mod_lpa.completions.sessionNotes', 'Session notes')}</CardTitle>
                      <CardDescription>
                        {t('mod_lpa.completions.sessionNotesHint', 'Optional overall notes for this audit session.')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder={t('mod_lpa.completions.notesPlaceholder', 'General observations...')}
                        className="min-h-28"
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setQuestionIndex((v) => Math.max(0, v - 1))}
                          disabled={questionIndex <= 0}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          {t('common.previous', 'Previous')}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setQuestionIndex((v) => Math.min(visibleResponses.length - 1, v + 1))}
                          disabled={questionIndex >= visibleResponses.length - 1}
                        >
                          {t('common.next', 'Next')}
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>

                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={handleComplete}
                        disabled={completeCompletion.isPending}
                      >
                        {completeCompletion.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {t('mod_lpa.completions.complete', 'Complete audit')}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full text-destructive"
                        onClick={handleCancel}
                        disabled={cancelCompletion.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {t('mod_lpa.completions.cancel', 'Cancel audit')}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                ) : (
                  <div className="min-h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                    {t('mod_lpa.completions.noQuestions', 'No active questions for this instance.')}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
